import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { isAudioSupported, playSmartAudio } from '../utils/audioUtils';
import { FaVolumeUp, FaVolumeMute, FaHeart, FaStar, FaArrowLeft, FaArrowRight, FaRedo, FaHome, FaChartLine, FaLightbulb, FaClock, FaSyncAlt, FaBullseye, FaBrain, FaCog, FaTimes, FaQuestion, FaCheck, FaQuoteLeft, FaPlay, FaPause, FaUndo, FaTrophy, FaFire, FaBookOpen, FaGamepad, FaKeyboard, FaEye, FaEyeSlash, FaRandom, FaFilter, FaSearch, FaGraduationCap, FaMedal, FaRocket, FaMoon, FaSun } from 'react-icons/fa';
import { API_BASE_URL } from '../config/api';
import '../components/Flashcard.css';

// Simple flashcard styles
const flashcardStyles = `
  .simple-flashcard {
    transition: all 0.3s ease;
    transform-style: preserve-3d;
  }
  .simple-flashcard:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  }
`;

interface Flashcard {
  id: string;
  persian: string;
  english: string;
  transliteration: string;
  category: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  audioUrl?: string;
  example?: string;
  lastReviewed?: Date;
  nextReview?: Date;
  mastery: number; // 0-100
  pathwayTitle?: string;
  lessonTitle?: string;
  icon?: string; // For animal cards
  color?: string; // For color cards background
  textColor?: string; // For color cards text
}

// Fallback data for when API calls fail
const fallbackCards: Flashcard[] = [
  {
    id: '1',
    persian: 'سلام',
    english: 'Hello',
    transliteration: 'Salaam',
    category: 'Greetings',
    difficulty: 'beginner',
    audioUrl: '/audio/vocabulary/greetings/hello.mp3',
    example: 'سلام! چطور هستید؟',
    mastery: 0
  },
  {
    id: '2',
    persian: 'کتاب',
    english: 'Book',
    transliteration: 'Ketaab',
    category: 'Education',
    difficulty: 'beginner',
    audioUrl: '/audio/vocabulary/nature/book.mp3',
    example: 'این کتاب خیلی خوب است',
    mastery: 0
  },
  {
    id: '3',
    persian: 'ماه',
    english: 'Moon',
    transliteration: 'Maah',
    category: 'Nature',
    difficulty: 'beginner',
    audioUrl: '/audio/vocabulary/nature/moon.mp3',
    example: 'ماه زیبا است',
    mastery: 0
  },
  {
    id: '4',
    persian: 'خوش آمدید',
    english: 'Welcome',
    transliteration: 'Khosh Aamadid',
    category: 'Greetings',
    difficulty: 'beginner',
    audioUrl: '/audio/vocabulary/greetings/welcome.mp3',
    example: 'خوش آمدید به خانه ما',
    mastery: 0
  },
  {
    id: '5',
    persian: 'سگ',
    english: 'Dog',
    transliteration: 'Sag',
    category: 'Animals',
    difficulty: 'beginner',
    audioUrl: '/audio/vocabulary/animals/dog.mp3',
    example: 'سگ دوست خوبی است',
    mastery: 0,
    icon: '🐕'
  },
  {
    id: '31',
    persian: 'گربه',
    english: 'Cat',
    transliteration: 'Gorbeh',
    category: 'Animals',
    difficulty: 'beginner',
    audioUrl: '/audio/vocabulary/animals/cat.mp3',
    example: 'گربه روی مبل خوابیده',
    mastery: 0,
    icon: '🐱'
  },
  {
    id: '32',
    persian: 'فیل',
    english: 'Elephant',
    transliteration: 'Fil',
    category: 'Animals',
    difficulty: 'beginner',
    audioUrl: '/audio/vocabulary/animals/elephant.mp3',
    example: 'فیل حیوان بزرگی است',
    mastery: 0,
    icon: '🐘'
  },
  {
    id: '33',
    persian: 'شیر',
    english: 'Lion',
    transliteration: 'Shir',
    category: 'Animals',
    difficulty: 'beginner',
    audioUrl: '/audio/vocabulary/animals/lion.mp3',
    example: 'شیر پادشاه جنگل است',
    mastery: 0,
    icon: '🦁'
  },
  {
    id: '34',
    persian: 'خرس',
    english: 'Bear',
    transliteration: 'Khers',
    category: 'Animals',
    difficulty: 'beginner',
    audioUrl: '/audio/vocabulary/animals/bear.mp3',
    example: 'خرس در جنگل زندگی می‌کند',
    mastery: 0,
    icon: '🐻'
  },
  {
    id: '35',
    persian: 'خرگوش',
    english: 'Rabbit',
    transliteration: 'Khargoosh',
    category: 'Animals',
    difficulty: 'beginner',
    audioUrl: '/audio/vocabulary/animals/rabbit.mp3',
    example: 'خرگوش سریع می‌دود',
    mastery: 0,
    icon: '🐰'
  },
  {
    id: '36',
    persian: 'پرنده',
    english: 'Bird',
    transliteration: 'Parandeh',
    category: 'Animals',
    difficulty: 'beginner',
    audioUrl: '/audio/vocabulary/animals/bird.mp3',
    example: 'پرنده در آسمان پرواز می‌کند',
    mastery: 0,
    icon: '🐦'
  },
  {
    id: '37',
    persian: 'ماهی',
    english: 'Fish',
    transliteration: 'Maahi',
    category: 'Animals',
    difficulty: 'beginner',
    audioUrl: '/audio/vocabulary/animals/fish.mp3',
    example: 'ماهی در آب شنا می‌کند',
    mastery: 0,
    icon: '🐠'
  },
  {
    id: '38',
    persian: 'اسب',
    english: 'Horse',
    transliteration: 'Asb',
    category: 'Animals',
    difficulty: 'beginner',
    audioUrl: '/audio/vocabulary/animals/horse.mp3',
    example: 'اسب حیوان قوی است',
    mastery: 0,
    icon: '🐴'
  },
  {
    id: '39',
    persian: 'گاو',
    english: 'Cow',
    transliteration: 'Gaav',
    category: 'Animals',
    difficulty: 'beginner',
    audioUrl: '/audio/vocabulary/animals/cow.mp3',
    example: 'گاو شیر می‌دهد',
    mastery: 0,
    icon: '🐄'
  },
  {
    id: '6',
    persian: 'سفید',
    english: 'White',
    transliteration: 'Sefid',
    category: 'Colors',
    difficulty: 'beginner',
    audioUrl: '/audio/vocabulary/colors/white.mp3',
    example: 'گربه سفید زیبا است',
    mastery: 0,
    color: '#FFFFFF',
    textColor: '#000000'
  },
  {
    id: '7',
    persian: 'سیاه',
    english: 'Black',
    transliteration: 'Siaah',
    category: 'Colors',
    difficulty: 'beginner',
    audioUrl: '/audio/vocabulary/colors/black.mp3',
    example: 'گربه سیاه زیبا است',
    mastery: 0,
    color: '#000000',
    textColor: '#FFFFFF'
  },
  {
    id: '8',
    persian: 'سبز',
    english: 'Green',
    transliteration: 'Sabz',
    category: 'Colors',
    difficulty: 'beginner',
    audioUrl: '/audio/vocabulary/colors/green.mp3',
    example: 'درخت سبز است',
    mastery: 0,
    color: '#00FF00',
    textColor: '#000000'
  },
  {
    id: '9',
    persian: 'سر',
    english: 'Head',
    transliteration: 'Sar',
    category: 'Body Parts',
    difficulty: 'beginner',
    audioUrl: '/audio/vocabulary/body-parts/head.mp3',
    example: 'سر من درد می‌کند',
    mastery: 0
  },
  {
    id: '10',
    persian: 'کوچک',
    english: 'Small',
    transliteration: 'Kuchak',
    category: 'Adjectives',
    difficulty: 'beginner',
    audioUrl: '/audio/vocabulary/colors/small.mp3',
    example: 'گربه کوچک',
    mastery: 0
  },
  {
    id: '11',
    persian: 'زیبا',
    english: 'Beautiful',
    transliteration: 'Zibaa',
    category: 'Adjectives',
    difficulty: 'beginner',
    audioUrl: '/audio/vocabulary/emotions/beautiful.mp3',
    example: 'دختر زیبا',
    mastery: 0
  },
  {
    id: '12',
    persian: 'خوشحال',
    english: 'Happy',
    transliteration: 'Khoshhaal',
    category: 'Emotions',
    difficulty: 'beginner',
    audioUrl: '/audio/vocabulary/emotions/happy.mp3',
    example: 'من خوشحال هستم',
    mastery: 0
  },
  {
    id: '13',
    persian: 'ناراحت',
    english: 'Sad',
    transliteration: 'Naaraahat',
    category: 'Emotions',
    difficulty: 'beginner',
    audioUrl: '/audio/vocabulary/emotions/sad.mp3',
    example: 'او ناراحت است',
    mastery: 0
  },
  {
    id: '14',
    persian: 'عصبانی',
    english: 'Angry',
    transliteration: 'Asabaanii',
    category: 'Emotions',
    difficulty: 'beginner',
    audioUrl: '/audio/vocabulary/emotions/angry.mp3',
    example: 'پدر عصبانی است',
    mastery: 0
  },
  {
    id: '14',
    persian: 'خسته',
    english: 'Tired',
    transliteration: 'Khasteh',
    category: 'Emotions',
    difficulty: 'beginner',
    example: 'من خسته هستم',
    mastery: 0
  },
  {
    id: '15',
    persian: 'گرسنه',
    english: 'Hungry',
    transliteration: 'Gorsneh',
    category: 'Emotions',
    difficulty: 'beginner',
    example: 'کودک گرسنه است',
    mastery: 0
  },
  {
    id: '16',
    persian: 'قرمز',
    english: 'Red',
    transliteration: 'Ghermez',
    category: 'Colors',
    difficulty: 'beginner',
    example: 'گل قرمز',
    mastery: 0,
    color: '#FF0000',
    textColor: '#FFFFFF'
  },
  {
    id: '17',
    persian: 'آبی',
    english: 'Blue',
    transliteration: 'Aabii',
    category: 'Colors',
    difficulty: 'beginner',
    example: 'آسمان آبی',
    mastery: 0,
    color: '#0000FF',
    textColor: '#FFFFFF'
  },
  {
    id: '18',
    persian: 'سبز',
    english: 'Green',
    transliteration: 'Sabz',
    category: 'Colors',
    difficulty: 'beginner',
    example: 'درخت سبز',
    mastery: 0,
    color: '#00FF00',
    textColor: '#000000'
  },
  {
    id: '19',
    persian: 'زرد',
    english: 'Yellow',
    transliteration: 'Zard',
    category: 'Colors',
    difficulty: 'beginner',
    example: 'خورشید زرد',
    mastery: 0,
    color: '#FFFF00',
    textColor: '#000000'
  },
  {
    id: '20',
    persian: 'سیاه',
    english: 'Black',
    transliteration: 'Siaah',
    category: 'Colors',
    difficulty: 'beginner',
    example: 'شب سیاه',
    mastery: 0,
    color: '#000000',
    textColor: '#FFFFFF'
  },
  {
    id: '21',
    persian: 'سفید',
    english: 'White',
    transliteration: 'Sefid',
    category: 'Colors',
    difficulty: 'beginner',
    example: 'برف سفید',
    mastery: 0,
    color: '#FFFFFF',
    textColor: '#000000'
  },
  {
    id: '40',
    persian: 'نارنجی',
    english: 'Orange',
    transliteration: 'Naaranjii',
    category: 'Colors',
    difficulty: 'beginner',
    audioUrl: '/audio/vocabulary/colors/orange.mp3',
    example: 'پرتقال نارنجی است',
    mastery: 0,
    color: '#FFA500',
    textColor: '#000000'
  },
  {
    id: '41',
    persian: 'بنفش',
    english: 'Purple',
    transliteration: 'Banafsh',
    category: 'Colors',
    difficulty: 'beginner',
    audioUrl: '/audio/vocabulary/colors/purple.mp3',
    example: 'گل بنفش زیبا است',
    mastery: 0,
    color: '#800080',
    textColor: '#FFFFFF'
  },
  {
    id: '42',
    persian: 'صورتی',
    english: 'Pink',
    transliteration: 'Sooratii',
    category: 'Colors',
    difficulty: 'beginner',
    audioUrl: '/audio/vocabulary/colors/pink.mp3',
    example: 'گل صورتی زیبا است',
    mastery: 0,
    color: '#FFC0CB',
    textColor: '#000000'
  },
  {
    id: '43',
    persian: 'قهوه‌ای',
    english: 'Brown',
    transliteration: 'Ghahveii',
    category: 'Colors',
    difficulty: 'beginner',
    audioUrl: '/audio/vocabulary/colors/brown.mp3',
    example: 'درخت قهوه‌ای است',
    mastery: 0,
    color: '#8B4513',
    textColor: '#FFFFFF'
  },
  {
    id: '44',
    persian: 'خاکستری',
    english: 'Gray',
    transliteration: 'Khaakestarii',
    category: 'Colors',
    difficulty: 'beginner',
    audioUrl: '/audio/vocabulary/colors/gray.mp3',
    example: 'ابر خاکستری است',
    mastery: 0,
    color: '#808080',
    textColor: '#FFFFFF'
  },
  {
    id: '22',
    persian: 'یک',
    english: 'One',
    transliteration: 'Yek',
    category: 'Numbers',
    difficulty: 'beginner',
    example: 'یک کتاب',
    mastery: 0
  },
  {
    id: '23',
    persian: 'دو',
    english: 'Two',
    transliteration: 'Do',
    category: 'Numbers',
    difficulty: 'beginner',
    example: 'دو دوست',
    mastery: 0
  },
  {
    id: '24',
    persian: 'سه',
    english: 'Three',
    transliteration: 'Seh',
    category: 'Numbers',
    difficulty: 'beginner',
    example: 'سه کودک',
    mastery: 0
  },
  {
    id: '25',
    persian: 'چهار',
    english: 'Four',
    transliteration: 'Chahaar',
    category: 'Numbers',
    difficulty: 'beginner',
    example: 'چهار درخت',
    mastery: 0
  },
  {
    id: '26',
    persian: 'پنج',
    english: 'Five',
    transliteration: 'Panj',
    category: 'Numbers',
    difficulty: 'beginner',
    example: 'پنج کتاب',
    mastery: 0
  },
  {
    id: '27',
    persian: 'سر',
    english: 'Head',
    transliteration: 'Sar',
    category: 'Body Parts',
    difficulty: 'beginner',
    example: 'سر بزرگ',
    mastery: 0
  },
  {
    id: '28',
    persian: 'دست',
    english: 'Hand',
    transliteration: 'Dast',
    category: 'Body Parts',
    difficulty: 'beginner',
    example: 'دست تمیز',
    mastery: 0
  },
  {
    id: '29',
    persian: 'پا',
    english: 'Foot',
    transliteration: 'Paa',
    category: 'Body Parts',
    difficulty: 'beginner',
    example: 'پای کوچک',
    mastery: 0
  },
  {
    id: '30',
    persian: 'چشم',
    english: 'Eye',
    transliteration: 'Cheshm',
    category: 'Body Parts',
    difficulty: 'beginner',
    example: 'چشم زیبا',
    mastery: 0
  }
];

const Flashcards: React.FC = () => {
  const [cards, setCards] = useState<Flashcard[]>(fallbackCards);
  const [filteredCards, setFilteredCards] = useState<Flashcard[]>(fallbackCards);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isFlipping, setIsFlipping] = useState(false);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [sessionStats, setSessionStats] = useState({
    reviewed: 0,
    mastered: 0,
    needsReview: 0,
    streak: 0
  });
  const [selectedPathway, setSelectedPathway] = useState('');
  const [selectedLesson, setSelectedLesson] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showTips, setShowTips] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  
  // Enhanced features state
  const [studyMode, setStudyMode] = useState<'normal' | 'quiz' | 'speed' | 'review'>('normal');
  const [darkMode, setDarkMode] = useState(false);
  const [showTransliteration, setShowTransliteration] = useState(true);
  const [autoPlayAudio, setAutoPlayAudio] = useState(false);
  const [shuffleMode, setShuffleMode] = useState(false);
  const [achievements, setAchievements] = useState<string[]>([]);
  const [points, setPoints] = useState(0);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [audioVolume, setAudioVolume] = useState(0.8);
  const [playbackSpeed, setPlaybackSpeed] = useState(1.0);
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);

  // Fetch and set cards with better error handling
  useEffect(() => {
    const fetchCards = async () => {
      try {
        setLoading(true);
        setApiError(null);
        
        console.log('Fetching word banks from API...');
        console.log('API URL:', `${API_BASE_URL}/api/words/wordbanks`);
        
        const response = await fetch(`${API_BASE_URL}/api/words/wordbanks`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          // Add timeout to prevent hanging requests
          signal: AbortSignal.timeout(10000) // 10 second timeout
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        const wordBanks = data.data || [];
        
        if (wordBanks.length > 0) {
          console.log(`Successfully fetched ${wordBanks.length} word banks`);
          const transformedCards = wordBanks.map((entry: any, idx: number) => ({
            id: entry._id || `${entry.word}-${idx}`,
            persian: entry.word || entry.persian,
            english: entry.translation || entry.english,
            transliteration: entry.pronunciation || '',
            category: entry.pathwayTitle || entry.category || 'General',
            difficulty: 'beginner',
            audioUrl: entry.audioUrl,
            example: entry.example || '',
            mastery: entry.mastery || 0,
            pathwayTitle: entry.pathwayTitle || '',
            lessonTitle: entry.lessonTitle || '',
          }));
          setCards(transformedCards);
        } else {
          console.log('No word banks found, using fallback data');
          setCards(fallbackCards);
          setApiError('No vocabulary data available from server. Using offline flashcards.');
        }
      } catch (error) {
        console.error('Error fetching word banks:', error);
        console.log('Using fallback flashcards data');
        setCards(fallbackCards);
        setApiError('Unable to connect to server. Using offline flashcards with 30+ Persian vocabulary words.');
      } finally {
        setLoading(false);
      }
    };

    fetchCards();
  }, []);

  // Filter cards based on selected filters
  useEffect(() => {
    let filtered = cards;
    if (selectedPathway) {
      filtered = filtered.filter(card => card.pathwayTitle === selectedPathway);
    }
    if (selectedLesson) {
      filtered = filtered.filter(card => card.lessonTitle === selectedLesson);
    }
    if (selectedCategory) {
      filtered = filtered.filter(card => card.category === selectedCategory);
    }
    setFilteredCards(filtered);
    setCurrentCardIndex(0);
  }, [cards, selectedPathway, selectedLesson, selectedCategory]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle keyboard events when flashcards are focused or when no input is focused
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement || event.target instanceof HTMLSelectElement) {
        return;
      }

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          // Go to previous card
          if (currentCardIndex > 0) {
            setCurrentCardIndex(currentCardIndex - 1);
            setShowAnswer(false); // Reset to front of card
          }
          break;
        case 'ArrowRight':
          event.preventDefault();
          // Go to next card
          if (currentCardIndex < filteredCards.length - 1) {
            setCurrentCardIndex(currentCardIndex + 1);
            setShowAnswer(false); // Reset to front of card
          }
          break;
        case ' ':
          event.preventDefault();
          // Space bar to flip card
          handleFlip();
          break;
        case 'a':
        case 'A':
          event.preventDefault();
          // A key to play audio
          handlePlayAudio();
          break;
        case 'd':
        case 'D':
          event.preventDefault();
          // D key to toggle dark mode
          setDarkMode(!darkMode);
          break;
        case 't':
        case 'T':
          event.preventDefault();
          // T key to toggle transliteration
          setShowTransliteration(!showTransliteration);
          break;
        case 's':
        case 'S':
          event.preventDefault();
          // S key to toggle shuffle mode
          setShuffleMode(!shuffleMode);
          break;
        case 'r':
        case 'R':
          event.preventDefault();
          // R key to restart session
          setCurrentCardIndex(0);
          setShowAnswer(false);
          setSessionStats({
            reviewed: 0,
            mastered: 0,
            needsReview: 0,
            streak: 0
          });
          break;
        case 'h':
        case 'H':
          event.preventDefault();
          // H key to show/hide keyboard shortcuts
          setShowKeyboardShortcuts(!showKeyboardShortcuts);
          break;
      }
    };

    // Add event listener
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup function to remove event listener
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentCardIndex, filteredCards.length, showAnswer, darkMode, showTransliteration, shuffleMode, showKeyboardShortcuts]);

  // Get unique values for filters
  const pathwayOptions = Array.from(new Set(cards.map(card => card.pathwayTitle).filter(Boolean)));
  const lessonOptions = Array.from(new Set(cards.filter(card => !selectedPathway || card.pathwayTitle === selectedPathway).map(card => card.lessonTitle).filter(Boolean)));
  const categoryOptions = Array.from(new Set(cards.map(card => card.category).filter(Boolean)));

  const handleFlip = () => {
    console.log('🔄 handleFlip called! Current showAnswer:', showAnswer);
    if (isFlipping) {
      console.log('⏳ Already flipping, ignoring click');
      return;
    }
    console.log('✅ Starting flip animation');
    setIsFlipping(true);
    setShowAnswer(!showAnswer);
    console.log('📝 showAnswer will be:', !showAnswer);
    setTimeout(() => {
      setIsFlipping(false);
      console.log('🏁 Flip animation complete');
    }, 300);
  };

  const handleResponse = (response: 'easy' | 'medium' | 'hard') => {
    const currentCard = filteredCards[currentCardIndex];
    const updatedCards = [...filteredCards];
    
    // Update mastery based on response
    let newMastery = currentCard.mastery;
    let streakChange = 0;
    
    switch (response) {
      case 'easy':
        newMastery = Math.min(100, currentCard.mastery + 20);
        streakChange = 1;
        break;
      case 'medium':
        newMastery = Math.min(100, currentCard.mastery + 10);
        streakChange = 1;
        break;
      case 'hard':
        newMastery = Math.max(0, currentCard.mastery - 5);
        streakChange = -1;
        break;
    }
    
    updatedCards[currentCardIndex] = {
      ...currentCard,
      mastery: newMastery,
      lastReviewed: new Date(),
      nextReview: new Date(Date.now() + (response === 'easy' ? 7 : response === 'medium' ? 3 : 1) * 24 * 60 * 60 * 1000)
    };
    
    setFilteredCards(updatedCards);
    setShowAnswer(false);
    
    // Update session stats
    setSessionStats(prev => ({
      ...prev,
      reviewed: prev.reviewed + 1,
      mastered: newMastery >= 90 ? prev.mastered + 1 : prev.mastered,
      needsReview: newMastery < 50 ? prev.needsReview + 1 : prev.needsReview,
      streak: Math.max(0, prev.streak + streakChange)
    }));
    
    // Move to next card
    if (currentCardIndex < filteredCards.length - 1) {
      setCurrentCardIndex(currentCardIndex + 1);
    }
  };

  const handlePlayAudio = async () => {
    const currentCard = filteredCards[currentCardIndex];
    if (!currentCard) return;
    
    console.log('🎵 handlePlayAudio called for:', currentCard.persian);
    console.log('🎵 Audio URL:', currentCard.audioUrl);
    
    try {
      setIsAudioPlaying(true);
      
      // Use playSmartAudio which handles fallbacks to TTS
      await playSmartAudio(currentCard.audioUrl, currentCard.persian);
      
      setIsAudioPlaying(false);
      
    } catch (error) {
      console.error('❌ Error in handlePlayAudio:', error);
      setIsAudioPlaying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg font-medium">Loading flashcards...</p>
        </div>
      </div>
    );
  }

  // Debug logging
  console.log('Flashcards component render:', {
    cardsCount: cards.length,
    filteredCardsCount: filteredCards.length,
    currentCardIndex,
    showAnswer,
    sessionStats
  });

  // Test function to verify flashcards are working
  const testFlashcards = () => {
    console.log('Testing flashcards functionality...');
    console.log('Current cards:', cards);
    console.log('Filtered cards:', filteredCards);
    console.log('Current card:', currentCard);
    console.log('Session stats:', sessionStats);
  };

  const currentCard = filteredCards[currentCardIndex];

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-blue-50 via-white to-purple-50'}`}>
      <style>{flashcardStyles}</style>
      {/* Enhanced Navigation */}
      <nav className={`${darkMode ? 'bg-gray-800' : 'bg-gradient-to-r from-blue-600 to-purple-600'} text-white shadow-xl backdrop-blur-sm`}>
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <div className="bg-white bg-opacity-20 p-2 rounded-xl">
              <FaGraduationCap className="text-2xl" />
            </div>
            <div>
              <span className="text-xl font-bold">Pirooz</span>
              <div className="text-xs opacity-80">Learn Persian</div>
            </div>
          </div>
          
          {/* Study Mode Selector */}
          <div className="hidden md:flex items-center space-x-2 bg-white bg-opacity-10 rounded-full p-1">
            {[
              { mode: 'normal', icon: FaBookOpen, label: 'Normal' },
              { mode: 'quiz', icon: FaGamepad, label: 'Quiz' },
              { mode: 'speed', icon: FaRocket, label: 'Speed' },
              { mode: 'review', icon: FaUndo, label: 'Review' }
            ].map(({ mode, icon: Icon, label }) => (
              <button
                key={mode}
                onClick={() => setStudyMode(mode as any)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                  studyMode === mode 
                    ? 'bg-white text-blue-600 shadow-lg' 
                    : 'text-white hover:bg-white hover:bg-opacity-20'
                }`}
              >
                <Icon className="inline ml-1" />
                {label}
              </button>
            ))}
          </div>
          
          <div className="flex items-center space-x-3">
            {/* Points Display */}
            <div className="hidden md:flex items-center space-x-2 bg-yellow-500 bg-opacity-20 px-3 py-1 rounded-full">
              <FaTrophy className="text-yellow-300" />
              <span className="text-sm font-bold">{points}</span>
            </div>
            
            {/* Streak Display */}
            <div className="hidden md:flex items-center space-x-2 bg-red-500 bg-opacity-20 px-3 py-1 rounded-full">
              <FaFire className="text-red-300" />
              <span className="text-sm font-bold">{currentStreak}</span>
            </div>
            
            {/* Dark Mode Toggle */}
            <button
              onClick={() => setDarkMode(!darkMode)}
              className="p-2 rounded-full bg-white bg-opacity-10 hover:bg-opacity-20 transition"
              title="Toggle dark/light mode (D)"
            >
              {darkMode ? <FaSun className="text-yellow-300" /> : <FaMoon className="text-blue-300" />}
            </button>
            
            {/* Keyboard Shortcuts Help */}
            <button
              onClick={() => setShowKeyboardShortcuts(!showKeyboardShortcuts)}
              className="p-2 rounded-full bg-white bg-opacity-10 hover:bg-opacity-20 transition"
              title="Keyboard shortcuts help (H)"
            >
              <FaKeyboard className="text-white" />
            </button>
            
            <Link to="/dashboard" className="bg-white text-blue-600 px-4 py-2 rounded-full font-medium hover:bg-blue-50 transition shadow-lg">
              Dashboard
            </Link>
          </div>
        </div>
      </nav>

      {/* API Error Banner */}
      {apiError && (
        <div className="container mx-auto px-4 py-2">
          <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded relative">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium">{apiError}</p>
              </div>
              <div className="ml-auto pl-3 flex space-x-2">
                <button
                  onClick={() => {
                    setApiError(null);
                    setLoading(true);
                    // Retry fetching cards
                    fetch(`${API_BASE_URL}/api/words/wordbanks`)
                      .then(res => res.json())
                      .then(data => {
                        const wordBanks = data.data || [];
                        if (wordBanks.length > 0) {
                          const transformedCards = wordBanks.map((entry: any, idx: number) => ({
                            id: entry._id || `${entry.word}-${idx}`,
                            persian: entry.word || entry.persian,
                            english: entry.translation || entry.english,
                            transliteration: entry.pronunciation || '',
                            category: entry.pathwayTitle || entry.category || 'General',
                            difficulty: 'beginner',
                            audioUrl: entry.audioUrl,
                            example: entry.example || '',
                            mastery: entry.mastery || 0,
                            pathwayTitle: entry.pathwayTitle || '',
                            lessonTitle: entry.lessonTitle || '',
                          }));
                          setCards(transformedCards);
                          setApiError(null);
                        }
                      })
                      .catch(() => {
                        setApiError('Retry failed. Using offline flashcards.');
                      })
                      .finally(() => setLoading(false));
                  }}
                  className="text-yellow-600 hover:text-yellow-800 text-sm font-medium"
                >
                  Retry
                </button>
                <button
                  onClick={() => setApiError(null)}
                  className="text-yellow-400 hover:text-yellow-600"
                >
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col xl:flex-row gap-8">
          {/* Left Sidebar */}
          <div className="w-full xl:w-1/4 space-y-6">
            {/* Enhanced Stats Card */}
            <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-xl p-6 border ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Your Progress</h3>
                <div className="flex items-center space-x-2">
                  <FaChartLine className={`${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                  <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Live</span>
                </div>
              </div>
              
              {/* Progress Stats Grid */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center p-3 bg-blue-50 dark:bg-blue-900 rounded-xl">
                  <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{sessionStats.reviewed}</div>
                  <div className="text-xs text-blue-600 dark:text-blue-300">Reviewed</div>
                </div>
                <div className="text-center p-3 bg-green-50 dark:bg-green-900 rounded-xl">
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">{sessionStats.mastered}</div>
                  <div className="text-xs text-green-600 dark:text-green-300">Mastered</div>
                </div>
                <div className="text-center p-3 bg-yellow-50 dark:bg-yellow-900 rounded-xl">
                  <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{sessionStats.needsReview}</div>
                  <div className="text-xs text-yellow-600 dark:text-yellow-300">Needs Review</div>
                </div>
              </div>
              
              {/* Session Progress */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Session Progress
                  </span>
                  <span className={`text-sm font-bold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                    {filteredCards.length === 0 ? 0 : currentCardIndex + 1} of {filteredCards.length}
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                  <div 
                    className="bg-gradient-to-r from-blue-500 to-purple-500 h-3 rounded-full transition-all duration-500 shadow-lg"
                    style={{ width: `${filteredCards.length === 0 ? 0 : ((currentCardIndex + 1) / filteredCards.length) * 100}%` }}
                  ></div>
                </div>
              </div>
              
              {/* Streak and Points */}
              <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-2">
                  <FaFire className="text-red-500" />
                  <span className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                    {currentStreak} days
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <FaTrophy className="text-yellow-500" />
                  <span className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                    {points} points
                  </span>
                </div>
              </div>
            </div>

            {/* Study Tips */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Study Tips 📚</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0 bg-blue-100 p-2 rounded-full">
                    <FaClock className="text-blue-600" />
                  </div>
                  <div className="mr-3">
                    <h4 className="font-medium text-gray-800">⏰ Best Time</h4>
                    <p className="text-sm text-gray-600">Morning is the best time for studying</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 bg-green-100 p-2 rounded-full">
                    <FaSyncAlt className="text-green-600" />
                  </div>
                  <div className="mr-3">
                    <h4 className="font-medium text-gray-800">🔄 Regular Review</h4>
                    <p className="text-sm text-gray-600">Review words at regular intervals</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 bg-purple-100 p-2 rounded-full">
                    <FaBullseye className="text-purple-600" />
                  </div>
                  <div className="mr-3">
                    <h4 className="font-medium text-gray-800">🎯 Full Focus</h4>
                    <p className="text-sm text-gray-600">Study without distractions</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Spaced Repetition Info */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Smart Flashcards 🃏</h3>
              <p className="text-sm text-gray-600 mb-4">
                Spaced repetition system for better learning
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0 bg-blue-100 p-2 rounded-full">
                    <FaBrain className="text-blue-600" />
                  </div>
                  <div className="mr-3">
                    <p className="text-sm text-blue-800">
                      This system is designed based on the forgetting curve to help you review words at the right time.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Flashcard Area */}
          <div className="w-full xl:w-2/4">
            <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-xl overflow-hidden border ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
              {/* Enhanced Header */}
              <div className={`p-6 border-b ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                      Persian Vocabulary Flashcards
                    </h2>
                    <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'} mt-1`}>
                      Study mode: {studyMode === 'normal' ? 'Normal' : studyMode === 'quiz' ? 'Quiz' : studyMode === 'speed' ? 'Speed' : 'Review'}
                    </p>
                  </div>
                  <div className="flex items-center space-x-3">
                    {/* Card Counter */}
                    <div className={`px-3 py-1 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {filteredCards.length === 0 ? 0 : currentCardIndex + 1}/{filteredCards.length}
                      </span>
                    </div>
                    
                    {/* Control Buttons */}
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setShowTransliteration(!showTransliteration)}
                        className={`p-2 rounded-full transition ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                        title="Show/hide pronunciation"
                      >
                        {showTransliteration ? <FaEye className="text-blue-600" /> : <FaEyeSlash className="text-gray-400" />}
                      </button>
                      
                      <button
                        onClick={() => setShuffleMode(!shuffleMode)}
                        className={`p-2 rounded-full transition ${shuffleMode ? 'bg-purple-100 text-purple-600' : darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                        title="Random mode"
                      >
                        <FaRandom className={shuffleMode ? 'text-purple-600' : 'text-gray-400'} />
                      </button>
                      
                      <button
                        onClick={() => setAutoPlayAudio(!autoPlayAudio)}
                        className={`p-2 rounded-full transition ${autoPlayAudio ? 'bg-green-100 text-green-600' : darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                        title="Auto-play audio"
                      >
                        <FaVolumeUp className={autoPlayAudio ? 'text-green-600' : 'text-gray-400'} />
                      </button>
                      
                      {/* Audio Settings Dropdown */}
                      <div className="relative group">
                        <button
                          className="p-2 rounded-full transition dark:hover:bg-gray-700 hover:bg-gray-100"
                          title="Audio settings"
                        >
                          <FaCog className="text-gray-400" />
                        </button>
                        <div className="absolute right-0 top-full mt-2 w-64 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                          <div className="space-y-4">
                            <div>
                              <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-2`}>
                                Volume: {Math.round(audioVolume * 100)}%
                              </label>
                              <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.1"
                                value={audioVolume}
                                onChange={(e) => setAudioVolume(parseFloat(e.target.value))}
                                className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                              />
                            </div>
                            <div>
                              <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-2`}>
                                Playback speed: {playbackSpeed}x
                              </label>
                              <input
                                type="range"
                                min="0.5"
                                max="2"
                                step="0.1"
                                value={playbackSpeed}
                                onChange={(e) => setPlaybackSpeed(parseFloat(e.target.value))}
                                className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Quick Stats */}
                <div className="flex items-center space-x-6 text-sm">
                  <div className="flex items-center space-x-1">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Currently studying</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <FaClock className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                    <span className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Estimated time: {Math.ceil((filteredCards.length - currentCardIndex) * 0.5)} minutes</span>
                  </div>
                </div>
              </div>

              {/* Enhanced Flashcard */}
              <div className="p-8">
                {currentCard ? (
                  <>
                    {/* Simple Flashcard - Front or Back */}
                    {!showAnswer ? (
                      // Front Card
                      <div 
                        className="simple-flashcard w-full h-80 mx-auto cursor-pointer mb-8 rounded-2xl shadow-2xl p-8 flex flex-col justify-center items-center relative overflow-hidden"
                        style={{
                          background: currentCard.category === 'Colors' && currentCard.color 
                            ? `linear-gradient(135deg, ${currentCard.color}, ${currentCard.color}dd)`
                            : 'linear-gradient(135deg, #3b82f6, #1d4ed8, #7c3aed)',
                          color: currentCard.category === 'Colors' && currentCard.textColor 
                            ? currentCard.textColor 
                            : 'white'
                        }}
                        onClick={handleFlip}
                        title="Click to see meaning"
                      >
                        {/* Background Pattern */}
                        <div className="absolute inset-0 opacity-10">
                          <div className="absolute top-4 right-4 w-20 h-20 border-2 border-current rounded-full"></div>
                          <div className="absolute bottom-4 left-4 w-16 h-16 border-2 border-current rounded-full"></div>
                          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 border-2 border-current rounded-full"></div>
                        </div>
                        
                        <div className="text-center relative z-10">
                          <div className="text-sm mb-4 font-medium opacity-80">New Word</div>
                          
                          {/* Animal Icon */}
                          {currentCard.category === 'Animals' && currentCard.icon && (
                            <div className="text-6xl mb-4">{currentCard.icon}</div>
                          )}
                          
                          <div className="text-5xl font-bold mb-6" style={{ fontFamily: 'Noto Naskh Arabic, serif' }}>
                            {currentCard.persian}
                          </div>
                          {showTransliteration && (
                            <div className="text-lg mb-4 font-medium opacity-80">
                              {currentCard.transliteration}
                            </div>
                          )}
                          <div className="text-sm mb-4 opacity-80">Click to see meaning</div>
                          
                          {/* Audio Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePlayAudio();
                            }}
                            className="p-3 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full transition-all"
                          >
                            {isAudioPlaying ? <FaPause className="text-xl" /> : <FaPlay className="text-xl" />}
                          </button>
                        </div>
                      </div>
                    ) : (
                      // Back Card
                      <div 
                        className="simple-flashcard w-full h-80 mx-auto cursor-pointer mb-8 rounded-2xl shadow-2xl p-8 flex flex-col justify-center items-center relative overflow-hidden"
                        style={{
                          background: currentCard.category === 'Colors' && currentCard.color 
                            ? `linear-gradient(135deg, ${currentCard.color}, ${currentCard.color}dd)`
                            : 'linear-gradient(135deg, #10b981, #059669, #047857)',
                          color: currentCard.category === 'Colors' && currentCard.textColor 
                            ? currentCard.textColor 
                            : 'white'
                        }}
                        onClick={handleFlip}
                        title="Click to flip back"
                      >
                        {/* Background Pattern */}
                        <div className="absolute inset-0 opacity-10">
                          <div className="absolute top-4 right-4 w-20 h-20 border-2 border-current rounded-full"></div>
                          <div className="absolute bottom-4 left-4 w-16 h-16 border-2 border-current rounded-full"></div>
                          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 border-2 border-current rounded-full"></div>
                        </div>
                        
                        <div className="text-center relative z-10">
                          <div className="text-sm mb-4 font-medium opacity-80">Meaning</div>
                          
                          {/* Animal Icon */}
                          {currentCard.category === 'Animals' && currentCard.icon && (
                            <div className="text-6xl mb-4">{currentCard.icon}</div>
                          )}
                          
                          <div className="text-3xl font-bold mb-4">{currentCard.english}</div>
                          {showTransliteration && (
                            <div className="text-lg mb-4 font-medium opacity-80">
                              {currentCard.transliteration}
                            </div>
                          )}
                          {currentCard.example && (
                            <div className="text-sm bg-white bg-opacity-20 rounded-xl px-4 py-3 mb-4">
                              <FaQuoteLeft className="inline ml-1" />
                              {currentCard.example}
                            </div>
                          )}
                          
                          {/* Category Badge */}
                          <div className="inline-block bg-white bg-opacity-20 px-3 py-1 rounded-full text-xs font-medium mb-4">
                            {currentCard.category}
                          </div>
                          
                          {/* Audio Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePlayAudio();
                            }}
                            className="p-3 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full transition-all mb-4"
                          >
                            {isAudioPlaying ? <FaPause className="text-xl" /> : <FaPlay className="text-xl" />}
                          </button>
                          
                          <div className="text-sm opacity-80">Click to flip back</div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-16">
                    <div className="text-8xl mb-6">📚</div>
                    <h3 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'} mb-4`}>No cards found</h3>
                    <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-8`}>Please change filters or select a new pathway</p>
                    <button 
                      onClick={() => {
                        setSelectedPathway('');
                        setSelectedLesson('');
                        setSelectedCategory('');
                      }}
                      className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-8 py-3 rounded-full font-medium hover:shadow-lg transition-all"
                    >
                      Clear Filters
                    </button>
                  </div>
                )}

                {/* Enhanced Difficulty Buttons */}
                {showAnswer && (
                  <div className="space-y-6">
                    <div className="text-center">
                      <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-800'} mb-4`}>
                        How well do you know this word?
                      </h3>
                    </div>
                    
                    <div className="flex justify-center space-x-4">
                      <button 
                        onClick={() => handleResponse('hard')}
                        className="group bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-8 py-4 rounded-2xl font-medium flex items-center space-x-3 shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                      >
                        <div className="bg-white bg-opacity-20 p-2 rounded-full">
                          <FaTimes className="text-xl" />
                        </div>
                        <div className="text-right">
                          <div className="font-bold">Hard</div>
                          <div className="text-xs opacity-80">Needs more practice</div>
                        </div>
                      </button>
                      
                      <button 
                        onClick={() => handleResponse('medium')}
                        className="group bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white px-8 py-4 rounded-2xl font-medium flex items-center space-x-3 shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                      >
                        <div className="bg-white bg-opacity-20 p-2 rounded-full">
                          <FaQuestion className="text-xl" />
                        </div>
                        <div className="text-right">
                          <div className="font-bold">Medium</div>
                          <div className="text-xs opacity-80">Somewhat familiar</div>
                        </div>
                      </button>
                      
                      <button 
                        onClick={() => handleResponse('easy')}
                        className="group bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white px-8 py-4 rounded-2xl font-medium flex items-center space-x-3 shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                      >
                        <div className="bg-white bg-opacity-20 p-2 rounded-full">
                          <FaCheck className="text-xl" />
                        </div>
                        <div className="text-right">
                          <div className="font-bold">Easy</div>
                          <div className="text-xs opacity-80">Completely familiar</div>
                        </div>
                      </button>
                    </div>
                  </div>
                )}

                {/* Enhanced Session Controls */}
                <div className="flex justify-between items-center pt-6 border-t border-gray-200 dark:border-gray-700">
                  <button 
                    onClick={() => setCurrentCardIndex(Math.max(0, currentCardIndex - 1))}
                    disabled={currentCardIndex === 0}
                    className={`px-6 py-3 rounded-xl font-medium transition-all ${
                      currentCardIndex === 0 
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700 hover:shadow-md'
                    }`}
                  >
                    <FaArrowRight className="inline ml-2" />
                    Previous
                  </button>
                  
                  <div className="flex items-center space-x-3">
                    <button 
                      onClick={() => {
                        setCurrentCardIndex(0);
                        setShowAnswer(false);
                        setSessionStats({ reviewed: 0, mastered: 0, needsReview: 0, streak: 0 });
                      }}
                      className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white px-6 py-3 rounded-xl font-medium shadow-lg hover:shadow-xl transition-all"
                    >
                      <FaUndo className="inline ml-2" />
                      Start New Session
                    </button>
                    
                    <Link 
                      to="/dashboard"
                      className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-xl font-medium transition-all hover:shadow-md"
                    >
                      <FaHome className="inline ml-2" />
                      Dashboard
                    </Link>
                  </div>
                  
                  <button 
                    onClick={() => setCurrentCardIndex(Math.min(filteredCards.length - 1, currentCardIndex + 1))}
                    disabled={currentCardIndex >= filteredCards.length - 1}
                    className={`px-6 py-3 rounded-xl font-medium transition-all ${
                      currentCardIndex >= filteredCards.length - 1 
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700 hover:shadow-md'
                    }`}
                  >
                    Next
                    <FaArrowLeft className="inline mr-2" />
                  </button>
                </div>
              </div>
            </div>

            {/* Session Complete */}
            {currentCardIndex >= filteredCards.length && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4 text-center">
                  <div className="bg-green-100 text-green-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FaCheck className="text-2xl" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">🎉 Session Complete!</h3>
                  <p className="text-gray-600 mb-6">You reviewed {sessionStats.reviewed} cards</p>
                  <div className="flex justify-center space-x-4">
                    <button 
                      onClick={() => {
                        setCurrentCardIndex(0);
                        setShowAnswer(false);
                        setSessionStats({ reviewed: 0, mastered: 0, needsReview: 0, streak: 0 });
                      }}
                      className="bg-blue-600 text-white px-6 py-2 rounded-full font-medium"
                    >
                      View Stats
                    </button>
                    <Link 
                      to="/dashboard"
                      className="bg-gray-200 text-gray-700 px-6 py-2 rounded-full font-medium"
                    >
                      Close
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Sidebar */}
          <div className="w-full xl:w-1/4 space-y-6">
            {/* Enhanced Filters */}
            <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-xl p-6 border ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Filters</h3>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="p-2 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition"
                >
                  <FaFilter />
                </button>
              </div>
              
              {showFilters && (
                <div className="space-y-4">
                  {/* Pathway Filter */}
                  <div>
                    <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-2`}>
                      Learning Pathway
                    </label>
                    <select
                      value={selectedPathway}
                      onChange={(e) => setSelectedPathway(e.target.value)}
                      className={`w-full p-3 rounded-xl border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-800'} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    >
                      <option value="">All Pathways</option>
                      {pathwayOptions.map(pathway => (
                        <option key={pathway} value={pathway}>{pathway}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Category Filter */}
                  <div>
                    <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-2`}>
                      Category
                    </label>
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className={`w-full p-3 rounded-xl border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-800'} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    >
                      <option value="">All Categories</option>
                      {categoryOptions.map(category => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Quick Actions */}
                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        setSelectedPathway('');
                        setSelectedLesson('');
                        setSelectedCategory('');
                      }}
                      className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-xl text-sm font-medium transition"
                    >
                      Clear All
                    </button>
                    <button
                      onClick={() => {
                        const shuffled = [...filteredCards].sort(() => Math.random() - 0.5);
                        setFilteredCards(shuffled);
                      }}
                      className="flex-1 bg-purple-100 hover:bg-purple-200 text-purple-700 px-4 py-2 rounded-xl text-sm font-medium transition"
                    >
                      <FaRandom className="inline ml-1" />
                      Random
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Enhanced Word of the Day */}
            <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-xl p-6 border ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Word of the Day</h3>
                <div className="bg-yellow-100 text-yellow-600 px-2 py-1 rounded-full text-xs font-bold">
                  Special
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900 dark:to-purple-900 rounded-xl p-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-700 dark:text-blue-300 mb-3" style={{ fontFamily: 'Noto Naskh Arabic, serif' }}>
                    آموزش
                  </div>
                  <div className="text-blue-600 dark:text-blue-400 font-semibold mb-2">Education</div>
                  <div className="text-sm text-blue-500 dark:text-blue-300 mb-4">/ɒːmuːzeʃ/</div>
                  <div className="text-sm text-blue-800 dark:text-blue-200 bg-white dark:bg-gray-800 bg-opacity-60 rounded-lg p-3">
                    "Education is the key to progress." - Teaching is more important than learning.
                  </div>
                </div>
              </div>
            </div>

            {/* Achievements */}
            <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-xl p-6 border ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Achievements</h3>
                <FaMedal className="text-yellow-500" />
              </div>
              
              <div className="space-y-3">
                {achievements.length > 0 ? (
                  achievements.map((achievement, index) => (
                    <div key={index} className="flex items-center space-x-3 p-3 bg-yellow-50 dark:bg-yellow-900 rounded-xl">
                      <FaTrophy className="text-yellow-500" />
                      <span className="text-sm font-medium text-yellow-700 dark:text-yellow-300">{achievement}</span>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4">
                    <div className="text-4xl mb-2">🏆</div>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      No achievements earned yet
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Progress Chart */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Weekly Progress</h3>
              <div className="relative">
                <svg width="100%" height="150" viewBox="0 0 300 150" className="mx-auto">
                  {/* Grid lines */}
                  <line x1="0" y1="120" x2="300" y2="120" stroke="#e5e7eb" strokeWidth="1" />
                  <line x1="0" y1="90" x2="300" y2="90" stroke="#e5e7eb" strokeWidth="1" />
                  <line x1="0" y1="60" x2="300" y2="60" stroke="#e5e7eb" strokeWidth="1" />
                  <line x1="0" y1="30" x2="300" y2="30" stroke="#e5e7eb" strokeWidth="1" />
                  <line x1="0" y1="0" x2="300" y2="0" stroke="#e5e7eb" strokeWidth="1" />
                  
                  {/* Data line */}
                  <polyline 
                    points="30,120 60,90 90,60 120,90 150,30 180,60 210,30 240,60 270,90" 
                    fill="none" 
                    stroke="#3b82f6" 
                    strokeWidth="3"
                  />
                  
                  {/* Data points */}
                  <circle cx="30" cy="120" r="4" fill="#3b82f6" />
                  <circle cx="60" cy="90" r="4" fill="#3b82f6" />
                  <circle cx="90" cy="60" r="4" fill="#3b82f6" />
                  <circle cx="120" cy="90" r="4" fill="#3b82f6" />
                  <circle cx="150" cy="30" r="4" fill="#3b82f6" />
                  <circle cx="180" cy="60" r="4" fill="#3b82f6" />
                  <circle cx="210" cy="30" r="4" fill="#3b82f6" />
                  <circle cx="240" cy="60" r="4" fill="#3b82f6" />
                  <circle cx="270" cy="90" r="4" fill="#3b82f6" />
                  
                  {/* X-axis labels */}
                  <text x="30" y="140" textAnchor="middle" fontSize="10" fill="#6b7280">S</text>
                  <text x="60" y="140" textAnchor="middle" fontSize="10" fill="#6b7280">S</text>
                  <text x="90" y="140" textAnchor="middle" fontSize="10" fill="#6b7280">M</text>
                  <text x="120" y="140" textAnchor="middle" fontSize="10" fill="#6b7280">T</text>
                  <text x="150" y="140" textAnchor="middle" fontSize="10" fill="#6b7280">W</text>
                  <text x="180" y="140" textAnchor="middle" fontSize="10" fill="#6b7280">T</text>
                  <text x="210" y="140" textAnchor="middle" fontSize="10" fill="#6b7280">F</text>
                  <text x="240" y="140" textAnchor="middle" fontSize="10" fill="#6b7280">S</text>
                  <text x="270" y="140" textAnchor="middle" fontSize="10" fill="#6b7280">S</text>
                </svg>
              </div>
              <div className="flex justify-between mt-4 text-xs text-gray-500">
                <span>Sat</span>
                <span>Sun</span>
                <span>Mon</span>
                <span>Tue</span>
                <span>Wed</span>
                <span>Thu</span>
                <span>Fri</span>
                <span>Sat</span>
                <span>Today</span>
              </div>
            </div>

            {/* Upcoming Reviews */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Upcoming Reviews</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center bg-gray-50 p-3 rounded-lg">
                  <div>
                    <div className="font-medium">Tomorrow</div>
                    <div className="text-xs text-gray-500">5 words</div>
                  </div>
                  <div className="bg-blue-100 text-blue-600 text-xs px-2 py-1 rounded-full">
                    New
                  </div>
                </div>
                <div className="flex justify-between items-center bg-gray-50 p-3 rounded-lg">
                  <div>
                    <div className="font-medium">Day After Tomorrow</div>
                    <div className="text-xs text-gray-500">3 words</div>
                  </div>
                </div>
                <div className="flex justify-between items-center bg-gray-50 p-3 rounded-lg">
                  <div>
                    <div className="font-medium">In 3 Days</div>
                    <div className="text-xs text-gray-500">8 words</div>
                  </div>
                  <div className="bg-yellow-100 text-yellow-600 text-xs px-2 py-1 rounded-full">
                    Important
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Keyboard Shortcuts Modal */}
      {showKeyboardShortcuts && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-w-2xl w-full mx-4 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                Keyboard Shortcuts
              </h3>
              <button
                onClick={() => setShowKeyboardShortcuts(false)}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition"
              >
                <FaTimes className="text-gray-500" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h4 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  Card Controls
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Flip card</span>
                    <kbd className="px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded text-sm">Space</kbd>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Previous card</span>
                    <kbd className="px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded text-sm">←</kbd>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Next card</span>
                    <kbd className="px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded text-sm">→</kbd>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Play audio</span>
                    <kbd className="px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded text-sm">A</kbd>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <h4 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  Responses
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Hard</span>
                    <kbd className="px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded text-sm">1</kbd>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Medium</span>
                    <kbd className="px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded text-sm">2</kbd>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Easy</span>
                    <kbd className="px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded text-sm">3</kbd>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <h4 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  Settings
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Dark mode</span>
                    <kbd className="px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded text-sm">D</kbd>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Show pronunciation</span>
                    <kbd className="px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded text-sm">T</kbd>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Random mode</span>
                    <kbd className="px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded text-sm">S</kbd>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <h4 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  Other
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Restart</span>
                    <kbd className="px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded text-sm">R</kbd>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Help</span>
                    <kbd className="px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded text-sm">H</kbd>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'} text-center`}>
                Press H or click the X button to close this window
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Flashcards; 